import type { CampaignRepository } from '../../domain/campaigns/repository';
import type { Task } from '../../types';
import { createCampaignRepository } from '../../platform/firebase/campaignRepository';
import { createTaskRepository } from '../../platform/firebase/taskRepository';
import type { TaskClaimScope, TaskReadRepository, Unsubscribe } from '../../domain/tasks/repository';
import type { CampaignStatusCounts } from '../../services/tasks';

export interface QueueReadRepositories {
  campaigns: CampaignRepository;
  tasks: TaskReadRepository;
}

export interface QueueCrossCampaignHandlers {
  onCampaigns: (campaigns: import('../../domain/campaigns/model').Campaign[]) => void;
  onMyOpenTasks: (tasks: Task[]) => void;
  onMyReviews: (tasks: Task[]) => void;
  onMyApprovedTasks: (tasks: Task[]) => void;
  onError: (error: Error) => void;
}

export interface QueueCampaignHandlers {
  onAwaitingReview: (tasks: Task[]) => void;
  onApprovedInCampaign: (tasks: Task[]) => void;
  onError: (error: Error) => void;
}

export interface QueueReadController {
  subscribeCrossCampaignReads(uid: string, handlers: QueueCrossCampaignHandlers): Unsubscribe;
  subscribeCampaignReads(
    campaignId: string,
    canSeeApproved: boolean,
    handlers: QueueCampaignHandlers,
  ): Unsubscribe;
  countAvailableTasksByType(campaignId: string, scopes: Array<string | TaskClaimScope>): Promise<Map<string, number>>;
  countUserTasksInCampaign(campaignId: string, uid: string): Promise<number>;
  countTasksByStatus(
    campaignId: string,
    taskTypeId?: string | null,
    domainId?: string | null,
  ): Promise<CampaignStatusCounts>;
  dispose(): void;
}

function cleanup(unsubscribes: Unsubscribe[]): Unsubscribe {
  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}

/**
 * Compose queue subscriptions in one feature-owned controller.  Each public
 * subscription returns an idempotent cleanup function, and dispose tears down
 * every subscription still owned by the controller.
 */
export function createQueueReadController(
  repositories: QueueReadRepositories = {
    campaigns: createCampaignRepository(),
    tasks: createTaskRepository(),
  },
): QueueReadController {
  const active = new Set<Unsubscribe>();
  const track = (unsubscribes: Unsubscribe[]): Unsubscribe => {
    const unsubscribe = cleanup(unsubscribes);
    active.add(unsubscribe);
    return () => {
      if (!active.has(unsubscribe)) return;
      active.delete(unsubscribe);
      unsubscribe();
    };
  };

  return {
    subscribeCrossCampaignReads(uid, handlers) {
      return track([
        repositories.campaigns.subscribeAll(handlers.onCampaigns, handlers.onError),
        repositories.tasks.subscribeMyOpenTasks(uid, handlers.onMyOpenTasks, handlers.onError),
        repositories.tasks.subscribeMyReviews(uid, handlers.onMyReviews, handlers.onError),
        repositories.tasks.subscribeMyApprovedTasks(uid, handlers.onMyApprovedTasks, handlers.onError),
      ]);
    },
    subscribeCampaignReads(campaignId, canSeeApproved, handlers) {
      const subscriptions = [
        repositories.tasks.subscribeAwaitingReviewInCampaign(
          campaignId,
          handlers.onAwaitingReview,
          handlers.onError,
        ),
      ];
      if (canSeeApproved) {
        subscriptions.push(
          repositories.tasks.subscribeApprovedTasksInCampaign(
            campaignId,
            handlers.onApprovedInCampaign,
            handlers.onError,
          ),
        );
      }
      return track(subscriptions);
    },
    countAvailableTasksByType(campaignId, scopes) {
      return repositories.tasks.countAvailableTasksByType(campaignId, scopes);
    },
    countUserTasksInCampaign(campaignId, uid) {
      return repositories.tasks.countUserTasksInCampaign(campaignId, uid);
    },
    countTasksByStatus(campaignId, taskTypeId, domainId) {
      return repositories.tasks.countTasksByStatus(campaignId, taskTypeId, domainId);
    },
    dispose() {
      Array.from(active).forEach((unsubscribe) => {
        active.delete(unsubscribe);
        unsubscribe();
      });
    },
  };
}
